// ─────────────────────────────────────────────────────────────────────────────
// Smart Command Engine — Maps parsed queries to data, routes, and responses
// No external AI API — pure rule-based intelligence
// Used by: AIAssistantDrawer.jsx
// ─────────────────────────────────────────────────────────────────────────────

import { parseQuery } from '../utils/queryParser';
import { analyzeRevenueTrend, analyzePatientGrowth } from '../utils/trendAnalytics';
import { generateMonthlyReport } from './reportGenerator';
import dashboardService from './dashboardService';
import invoiceService from './invoiceService';
import patientService from './patientService';
import predictiveService from './predictiveService';
import { mockEnabled } from '../mockData/mockConfig';
import { getCurrencySymbol, formatMoney } from '../utils/formatters';

// Mock data imports
import { mockOverviewGraph, mockMembersGraph, mockDashboardCounts } from '../mockData/mockDashboard';
import { mockDashboardInsights, mockOverdueInvoices } from '../mockData/mockAnalytics';
import { mockInvoiceListResponse } from '../mockData/mockInvoices';
import { mockPatientListResponse } from '../mockData/mockPatients';
import { mockTherapistPerformance } from '../mockData/mockPredictive';

// ── Data fetcher layer ──────────────────────────────────────────────────────

// A failed call is a failure, not a reason to show the mock data: that fallback used to
// hand a role without the permission (a 403) — or anyone, on a server error — made-up
// figures as if they were the clinic's. executeCommand turns the error into a plain answer.
const unwrap = (res) => {
  if (res?.rescode !== 1) throw new Error(res?.message || 'Request failed');
  return res.data || {};
};

const fetchData = async (type, params = {}) => {
  // USE_MOCK, or a demo session ("Explore Live Demo"), where apiService would answer from
  // fixed mocks that ignore the parameters these answers depend on.
  if (mockEnabled()) return getMockData(type, params);

  switch (type) {
    case 'overview':
      return unwrap(await dashboardService.getOverviewGraph({}));
    case 'members':
      return unwrap(await dashboardService.getMembersGraph({}));
    case 'insights':
      return unwrap(await dashboardService.getInsights({})).insights || [];
    case 'invoices':
      // Every invoice, not the first page of 100: /invoice/list builds the whole list
      // before it pages it, so this costs payload, not extra queries.
      return unwrap(await invoiceService.getList({ page: 1, perPage: 100000, ...params }));
    case 'overdue':
      return unwrap(await invoiceService.getOverdueInvoices({}));
    case 'patients':
      return unwrap(await patientService.getList(params));
    case 'counts':
      return unwrap(await dashboardService.getCounts({}));
    case 'performance':
      return unwrap(await predictiveService.getTherapistPerformance({}));
    default:
      return {};
  }
};

// The mock patient list answered the way /admin/patient/list answers: statsOnly gives the
// counts, isActive filters, perPage pages.
const mockPatients = (params) => {
  const all = mockPatientListResponse.data.users || [];
  if (params.statsOnly) {
    const active = all.filter(p => p.IsActive === 1 && Number(p.is_evaluation) !== 1).length;
    const evaluation = all.filter(p => p.IsActive === 1 && Number(p.is_evaluation) === 1).length;
    return { stats: { total: all.length, active, evaluation, inactive: all.length - active - evaluation } };
  }
  const users = params.isActive === undefined ? all : all.filter(p => p.IsActive === params.isActive);
  return { userCount: users.length, users: params.perPage ? users.slice(0, params.perPage) : users };
};

const getMockData = (type, params = {}) => {
  switch (type) {
    case 'overview': return mockOverviewGraph.data;
    case 'members': return mockMembersGraph.data;
    case 'insights': return mockDashboardInsights.data.insights;
    case 'invoices': return mockInvoiceListResponse.data;
    case 'overdue': return mockOverdueInvoices.data;
    case 'patients': return mockPatients(params);
    case 'counts': return mockDashboardCounts.data;
    case 'performance': return mockTherapistPerformance;
    default: return {};
  }
};

// The clinic's day (IST), as utils/clinicDay.clinicToday() reads it on the server.
const clinicTodayStr = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

// ── Route mapping ───────────────────────────────────────────────────────────

const ROUTE_MAP = {
  invoice_filter: '/portal/invoice',
  revenue: '/portal/income',
  patient: '/portal/allpatient',
  appointment: '/portal/appointment',
  therapist: '/portal/employeelist',
  therapy: '/portal/appointment',
  expense: '/portal/expense',
  report: '/portal',
  trend: '/portal',
  cancellation: '/portal/appointment',
  attendance: '/portal/attendance',
  salary: '/portal/salary-management',
  waiting_list: '/portal/waiting-list',
  services: '/portal/services',
  session_types: '/portal/session-types',
  pricing: '/portal/billing/pricing-plans',
  settlement: '/portal/billing/settlement',
  predictive: '/portal/predictive-intelligence',
  clinical: '/portal/therapy-intelligence',
  designation: '/portal/designations',
  support: '/portal/help-support',
  referrals: '/portal/referrals',
  wallet_credits: '/portal/wallet-credits',
  guests: '/portal/guests',
  group_sessions: '/portal/group-sessions',
  leave: '/portal/leave-management',
  roles: '/portal/rolelist',
};

// Who may open each page, as Router.js guards it (PermissionRoute). A role that can't
// open a page gets told so instead of a button that bounces it back to the Dashboard.
// 'admin' = adminOnly. Pages not listed are open to every signed-in user.
const ROUTE_PERMISSION = {
  '/portal/invoice': ['view_finance', 'view_invoices'],
  '/portal/income': 'view_income_expense',
  '/portal/expense': 'view_income_expense',
  '/portal/employeelist': 'view_employees',
  '/portal/attendance': 'manage_attendance',
  '/portal/salary-management': 'view_salary',
  '/portal/leave-management': 'view_employees',
  '/portal/services': 'view_services',
  '/portal/session-types': 'view_session_types',
  '/portal/designations': 'view_designations',
  '/portal/billing/pricing-plans': 'manage_billing',
  '/portal/billing/settlement': ['manage_billing', 'manage_invoices'],
  '/portal/wallet-credits': ['manage_billing', 'manage_invoices'],
  '/portal/referrals': 'view_referrals',
  '/portal/predictive-intelligence': 'view_intelligence',
  '/portal/therapy-intelligence': 'view_intelligence',
  '/portal/rolelist': 'admin',
};

const routePermission = (route) => {
  if (!route) return null;
  if (route.startsWith('/portal/predictive-intelligence')) return ROUTE_PERMISSION['/portal/predictive-intelligence'];
  return ROUTE_PERMISSION[route] || null;
};

// access = { isAdmin, permissions } from the signed-in user (AIAssistantDrawer).
// RoleId 1 and 2 open everything, as PermissionRoute and checkPermission allow.
const canOpen = (route, access) => {
  const needed = routePermission(route);
  if (!needed || !access || access.isAdmin) return true;
  if (needed === 'admin') return false;
  const anyOf = Array.isArray(needed) ? needed : [needed];
  return anyOf.some((p) => (access.permissions || []).includes(p));
};

const isForbidden = (error) => error?.response?.status === 403;

// The clinic's revenue — the Overview figures (dashOverviewGraph) — is view_finance's,
// "revenue on the Dashboard" in config/permissionCatalog.js. Reception holds view_invoices,
// which that route admitted until 2026-10-13 (now view_finance only); she never sees the Overview card or the month's
// takings (dashCounts keeps those Admin-only), so the assistant does not ask for them
// on her behalf (owner 2026-10-12).
const holds = (access, slug) => !access || access.isAdmin || (access.permissions || []).includes(slug);
const canSeeRevenue = (access) => holds(access, 'view_finance');

// A part of an answer the viewer may not have: a 403 becomes `fallback`, anything else throws.
const optional = (promise, fallback) => promise.catch((e) => {
  if (isForbidden(e)) return fallback;
  throw e;
});

// ── Command handlers ────────────────────────────────────────────────────────

const handleInvoiceFilter = async (parsed) => {
  const invoiceData = await fetchData('invoices');
  const invoices = invoiceData.invoices || [];
  const filters = parsed.filters;

  let filtered = [...invoices];

  // Apply amount filters
  if (filters.remaining_gt) {
    filtered = filtered.filter(inv => inv.remaining_amount > filters.remaining_gt);
  }
  if (filters.remaining_lt) {
    filtered = filtered.filter(inv => inv.remaining_amount < filters.remaining_lt);
  }
  if (filters.remaining_min !== undefined && filters.remaining_max !== undefined) {
    filtered = filtered.filter(inv => inv.remaining_amount >= filters.remaining_min && inv.remaining_amount <= filters.remaining_max);
  }
  if (filters.paymentStatus === 'pending' || filters.paymentStatus === 'overdue') {
    // CLOSED_UNPAID (a cancelled series' old dues) is not collectible now — the Invoice
    // page's Pending figure leaves it out too (AdminUsersController.invoiceList).
    filtered = filtered.filter(inv => inv.remaining_amount > 0 && inv.status !== 'CLOSED_UNPAID');
  }
  if (filters.paymentStatus === 'paid') {
    filtered = filtered.filter(inv => inv.remaining_amount <= 0);
  }
  if (filters.therapy) {
    filtered = filtered.filter(inv =>
      inv.Category?.toLowerCase().includes(filters.therapy.toLowerCase())
    );
  }

  const totalPending = filtered
    .filter(inv => inv.status !== 'CLOSED_UNPAID')
    .reduce((sum, inv) => sum + Math.max(0, inv.remaining_amount), 0);
  // The sum is the Invoices page's Pending total. /invoice/list sends counts: null to a
  // viewer who may not see those totals (canSeeBillingTotals) — Reception works the list
  // without them by default (owner 2026-10-02) — so she gets the rows, not the sum.
  const canSeeTotals = invoiceData.counts !== null;

  return {
    type: 'data_summary',
    category: 'finance',
    title: 'Invoice Filter Results',
    message: filtered.length > 0
      ? `Found ${filtered.length} invoice(s) matching your criteria.${canSeeTotals ? ` Total pending: ${getCurrencySymbol()}${formatMoney(totalPending)}.` : ''}`
      : 'No invoices found matching your criteria.',
    data: {
      count: filtered.length,
      totalPending: canSeeTotals ? totalPending : null,
      invoices: filtered.slice(0, 5).map(inv => ({
        id: inv.invoice_id,
        amount: inv.total_amount,
        paid: inv.paid_amount_sum,
        remaining: inv.remaining_amount,
        category: inv.Category,
      })),
    },
    route: ROUTE_MAP.invoice_filter,
    actionLabel: 'View Invoices',
  };
};

const handleRevenue = async (parsed, access) => {
  if (!canSeeRevenue(access)) return noAccessResponse('Revenue');
  // Payments are the income ledger by month (dashOverviewGraph) — the same money the
  // Dashboard's revenue card counts, so nothing needs to be added from the invoices.
  const overviewData = await fetchData('overview');
  const payments = overviewData.Payments || [];
  const trend = analyzeRevenueTrend(payments); // year to date: total and peak month
  const currentMonth = new Date().getMonth();
  // The running month is month to date, so the trend and the month-over-month figure use
  // closed months only: against a whole previous month, month to date read as a fall on
  // most days of every month.
  const closed = analyzeRevenueTrend(payments.slice(0, currentMonth));

  const { timeframe } = parsed.filters;
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const monthLabel = (i) => (i === currentMonth ? `${monthNames[i]} (so far)` : monthNames[i]);

  let focusRevenue = trend.totalRevenue;
  let focusPeriod = 'Year to Date';

  // Check for specific month
  const monthIndex = monthNames.findIndex(m => m.toLowerCase() === timeframe);
  if (monthIndex >= 0 && payments[monthIndex] !== undefined) {
    focusRevenue = payments[monthIndex];
    focusPeriod = monthLabel(monthIndex);
  } else if (timeframe === 'last_month') {
    const lastIdx = currentMonth - 1;
    if (lastIdx >= 0) {
      focusRevenue = payments[lastIdx];
      focusPeriod = monthNames[lastIdx];
    }
  } else if (timeframe === 'this_month') {
    focusRevenue = payments[currentMonth];
    focusPeriod = monthLabel(currentMonth);
  }

  const hasClosedPair = currentMonth >= 2 && closed.trend !== 'insufficient_data';
  const trendNote = hasClosedPair
    ? ` Over the closed months the trend is ${closed.trend}; ${monthNames[currentMonth - 1]} was ${closed.monthOverMonth > 0 ? '+' : ''}${closed.monthOverMonth}% against ${monthNames[currentMonth - 2]}.`
    : '';

  // Check for therapy-specific revenue
  let therapyNote = '';
  if (parsed.filters.therapy) {
    const invoiceData = await fetchData('invoices');
    const invoices = invoiceData.invoices || [];
    const therapyInvoices = invoices.filter(inv =>
      inv.Category?.toLowerCase().includes(parsed.filters.therapy.toLowerCase())
    );
    const therapyRevenue = therapyInvoices.reduce((sum, inv) => sum + inv.total_amount, 0);
    therapyNote = ` ${parsed.filters.therapy} contributed ${getCurrencySymbol()}${formatMoney(therapyRevenue)} from ${therapyInvoices.length} invoice(s).`;
  }

  return {
    type: 'data_summary',
    category: 'finance',
    title: `Revenue — ${focusPeriod}`,
    message: `Revenue for ${focusPeriod}: ${getCurrencySymbol()}${formatMoney(focusRevenue)}.${trendNote}${therapyNote}`,
    data: {
      revenue: focusRevenue,
      period: focusPeriod,
      trend: closed.trend,
      monthOverMonth: hasClosedPair ? closed.monthOverMonth : null,
      confidence: closed.confidence_score,
      peakMonth: trend.peakMonth,
    },
    route: ROUTE_MAP.revenue,
    actionLabel: 'View Income',
  };
};

const handlePatient = async (parsed) => {
  const filters = parsed.filters;

  // "patients with pending above 5000" is about money owed, which lives on invoices — the
  // patient list has no amount to filter, so it answered with every patient.
  if (filters.remaining_gt !== undefined || filters.remaining_lt !== undefined
    || filters.remaining_min !== undefined || ['pending', 'overdue', 'paid'].includes(filters.paymentStatus)) {
    return handleInvoiceFilter(parsed);
  }

  // Counts come from the whole clinic (statsOnly), never from one page of rows: the list
  // pages 100 at a time, active patients first, so counting a page found no inactive
  // patients at all once a clinic had 100 active ones.
  const [statsData, membersData] = await Promise.all([
    fetchData('patients', { statsOnly: true }),
    fetchData('members'),
  ]);
  const stats = statsData.stats || {};
  const members = membersData.Members || [];

  let rows = [];
  let count = stats.total || 0;
  let statusLabel = 'All';

  if (filters.status === 'inactive') {
    rows = (await fetchData('patients', { isActive: 0, perPage: 5 })).users || [];
    count = stats.inactive || 0;
    statusLabel = 'Inactive';
  } else if (filters.status === 'active') {
    // BR-PAT-2: a patient booked only for an Evaluation is not an active patient yet.
    rows = ((await fetchData('patients', { isActive: 1, perPage: 25 })).users || [])
      .filter(p => Number(p.is_evaluation) !== 1);
    count = stats.active || 0;
    statusLabel = 'Active';
  } else if (filters.status === 'new') {
    // Joined this month: the members graph counts joins per clinic month.
    const today = clinicTodayStr();
    rows = ((await fetchData('patients', {})).users || [])
      .filter(p => String(p.joining_date || '').slice(0, 7) === today.slice(0, 7));
    const monthJoins = members[Number(today.slice(5, 7)) - 1];
    count = typeof monthJoins === 'number' ? monthJoins : rows.length;
    statusLabel = 'New (this month)';
  } else {
    rows = (await fetchData('patients', { perPage: 5 })).users || [];
  }

  const growth = analyzePatientGrowth(members);

  return {
    type: 'data_summary',
    category: 'patient',
    title: `${statusLabel} Patients`,
    message: `Found ${count} ${statusLabel.toLowerCase()} patient(s). Total patients: ${stats.total || 0}${stats.evaluation ? ` (${stats.evaluation} in Evaluation)` : ''}. Growth trend: ${growth.trend}.`,
    data: {
      count,
      total: stats.total || 0,
      growth: growth.trend,
      patients: rows.slice(0, 5).map(p => ({
        id: p.UserId,
        name: `${p.FirstName} ${p.LastName || ''}`.trim(),
        phone: p.Phone,
        status: !p.IsActive ? 'Inactive' : Number(p.is_evaluation) === 1 ? 'Evaluation' : 'Active',
      })),
    },
    route: ROUTE_MAP.patient,
    actionLabel: 'View Patients',
  };
};

const handleTherapist = async (parsed, access) => {
  // The Therapist Performance ranking (performanceService.getTherapistPerformance): active
  // therapists scored on all their sessions so far. This used to read a calendar feed that
  // was never fetched outside mock mode, so every therapist showed 0 sessions.
  const performance = await fetchData('performance');
  const rankings = performance.rankings || [];
  const scored = rankings.filter(r => r.tier !== 'new');
  const limit = parsed.filters.limit || 3;
  const top = scored[0];
  // The Intelligence page for a role that may open it; otherwise the Staff List, whose
  // Performance Leaderboard shows the same ranking (EmployeeList.jsx).
  const perfRoute = '/portal/predictive-intelligence/therapist-performance';
  const onPerfPage = canOpen(perfRoute, access);

  return {
    type: 'data_summary',
    category: 'appointment',
    title: 'Therapist Performance',
    // sessions_completed is every booked session less the absences (performanceService), so
    // it also holds booked days still to come and cancelled ones: "on record", not "completed".
    message: top
      ? `Top therapist by performance score: ${top.therapist_name} (score ${top.overall_score}, ${top.sessions_completed} sessions on record). ${rankings.length} active therapist(s) ranked.`
      : rankings.length > 0
        ? `${rankings.length} active therapist(s), but none has enough sessions yet to be scored.`
        : 'No active therapists found.',
    data: {
      topPerformers: (scored.length > 0 ? scored : rankings).slice(0, limit).map(r => ({
        id: r.therapist_id,
        name: r.therapist_name,
        sessions: r.sessions_completed,
      })),
      totalDoctors: rankings.length,
    },
    route: onPerfPage ? perfRoute : ROUTE_MAP.therapist,
    actionLabel: onPerfPage ? 'View Therapist Performance' : 'View Staff List',
  };
};

const handleTherapyQuery = async (parsed) => {
  const invoiceData = await fetchData('invoices');
  const invoices = invoiceData.invoices || [];
  const therapy = parsed.filters.therapy;

  // Revenue and pending per therapy are the amount per category, which the Invoices page
  // shows only with its totals; /invoice/list sends counts: null to a viewer without them.
  if (invoiceData.counts === null) {
    return noAccessResponse(therapy ? `${therapy} Summary` : 'Therapy-wise Breakdown');
  }

  if (!therapy) {
    // Show therapy-wise breakdown
    const breakdown = {};
    invoices.forEach(inv => {
      const cat = inv.Category || 'Other';
      if (!breakdown[cat]) breakdown[cat] = { count: 0, revenue: 0, pending: 0 };
      breakdown[cat].count++;
      breakdown[cat].revenue += inv.total_amount;
      breakdown[cat].pending += inv.remaining_amount;
    });

    const sorted = Object.entries(breakdown)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.revenue - a.revenue);

    return {
      type: 'data_summary',
      category: 'appointment',
      title: 'Therapy-wise Breakdown',
      message: `${sorted.length} therapy types active. Top: ${sorted[0]?.name || 'N/A'} with ${getCurrencySymbol()}${sorted[0]?.revenue?.toLocaleString('en-IN') || '0'} revenue.`,
      data: { therapies: sorted },
      route: ROUTE_MAP.therapy,
      actionLabel: 'View Appointments',
    };
  }

  // Specific therapy filter
  const therapyInvoices = invoices.filter(inv =>
    inv.Category?.toLowerCase().includes(therapy.toLowerCase())
  );
  const totalRevenue = therapyInvoices.reduce((sum, inv) => sum + inv.total_amount, 0);
  const totalPending = therapyInvoices.reduce((sum, inv) => sum + inv.remaining_amount, 0);

  return {
    type: 'data_summary',
    category: 'appointment',
    title: `${therapy} Summary`,
    message: `${therapy}: ${therapyInvoices.length} invoice(s), ${getCurrencySymbol()}${totalRevenue.toLocaleString('en-IN')} total revenue, ${getCurrencySymbol()}${totalPending.toLocaleString('en-IN')} pending.`,
    data: {
      therapy,
      invoiceCount: therapyInvoices.length,
      totalRevenue,
      totalPending,
    },
    route: ROUTE_MAP.therapy,
    actionLabel: 'View Appointments',
  };
};

// The insights feed is view_analytics. A role without it — the default Accountant and
// Therapist — can still own the page an answer points to, so it is sent there instead of
// being told it has no access, and is never told "no alert" for figures nobody checked.
const pageAnswer = (title, category, message, route, actionLabel) => ({
  type: 'data_summary', category, title, message, data: null, route, actionLabel,
});

const handleExpense = async () => {
  const insights = await optional(fetchData('insights'), null);
  if (!insights) return pageAnswer('Expense Overview', 'finance', 'Open the Expenses page for the clinic\'s expenses.', ROUTE_MAP.expense, 'View Expenses');
  const expenseInsight = insights.find(i => i.type === 'expense_ratio');

  return {
    type: 'data_summary',
    category: 'finance',
    title: 'Expense Overview',
    message: expenseInsight
      ? expenseInsight.message
      : 'Expense data not available. Navigate to expenses for detailed breakdown.',
    data: {
      expenseRatio: expenseInsight?.value || null,
    },
    route: ROUTE_MAP.expense,
    actionLabel: 'View Expenses',
  };
};

const handleReport = async (parsed, access) => {
  // The revenue (canSeeRevenue), the insights feed (view_analytics), the invoice totals
  // (canSeeBillingTotals) and overdue invoices (view_finance) are parts of the report, not
  // all of it: a role without one gets the rest — revenue and empty sections left out, the
  // other lines "—", and no all-clear for figures it could not see. A part a role cannot
  // have is not asked for, so it does not collect a 403 for it.
  const [overviewData, membersData, insights, invoiceData, overdueData] = await Promise.all([
    canSeeRevenue(access) ? optional(fetchData('overview'), null) : Promise.resolve(null),
    fetchData('members'),
    holds(access, 'view_analytics') ? optional(fetchData('insights'), null) : Promise.resolve(null),
    optional(fetchData('invoices', { perPage: 1 }), { counts: null }), // only its counts are used
    holds(access, 'view_finance') ? optional(fetchData('overdue'), null) : Promise.resolve(null),
  ]);

  const report = generateMonthlyReport({
    // null leaves the revenue out of the report (reportGenerator).
    monthlyPayments: overviewData ? (overviewData.Payments || []) : null,
    monthlyMembers: membersData.Members || [],
    // null = this role cannot read the insights: their lines show "—", not "No alert".
    insights: Array.isArray(insights) ? insights : null,
    // null when the viewer may not see the clinic's totals (invoiceList canSeeBillingTotals).
    invoiceCounts: invoiceData.counts || null,
    overdueData,
    canSeeFinance: holds(access, 'view_finance'),
  });

  return {
    type: 'report',
    category: 'analytics',
    title: report.title,
    message: 'Monthly report generated successfully. Review sections below.',
    data: report,
    route: ROUTE_MAP.report,
    actionLabel: 'View Dashboard',
  };
};

const handleTrend = async (parsed, access) => {
  // Check if query is about specific metric
  const isRevenue = parsed.metadata?.allIntents?.revenue;
  const isPatient = parsed.metadata?.allIntents?.patient;

  // The revenue trend only for a viewer who may see revenue (canSeeRevenue); anyone else
  // asking about trends gets the patient growth trend, with no revenue in the answer.
  if (isRevenue && !canSeeRevenue(access)) return noAccessResponse('Revenue Trend Analysis');
  const [overviewData, membersData] = await Promise.all([
    canSeeRevenue(access) ? optional(fetchData('overview'), null) : Promise.resolve(null),
    fetchData('members'),
  ]);
  if (isRevenue && !overviewData) return noAccessResponse('Revenue Trend Analysis');
  const members = membersData.Members || [];

  // Closed months only: the running month is month to date (see handleRevenue).
  const currentMonth = new Date().getMonth();
  const revenueTrend = overviewData ? analyzeRevenueTrend((overviewData.Payments || []).slice(0, currentMonth)) : null;
  const patientTrend = analyzePatientGrowth(members.slice(0, currentMonth));

  let focusTrend = revenueTrend;
  let focusLabel = 'Revenue';

  if (!revenueTrend || (isPatient && !isRevenue)) {
    focusTrend = patientTrend;
    focusLabel = 'Patient Growth';
  }

  return {
    type: 'trend_analysis',
    category: 'analytics',
    title: `${focusLabel} Trend Analysis`,
    message: focusTrend.trend === 'insufficient_data'
      ? `Not enough closed months yet for a ${focusLabel.toLowerCase()} trend.`
      : `${focusLabel} trend over the closed months: ${focusTrend.trend}. Confidence: ${focusTrend.confidence_score}%. Change: ${focusTrend.change > 0 ? '+' : ''}${focusTrend.change}%.`,
    data: {
      ...(revenueTrend ? {
        revenue: {
          trend: revenueTrend.trend,
          confidence_score: revenueTrend.confidence_score,
          change: revenueTrend.monthOverMonth ?? 0,
        },
      } : {}),
      patients: {
        trend: patientTrend.trend,
        confidence_score: patientTrend.confidence_score,
        change: patientTrend.change ?? 0,
      },
    },
    route: ROUTE_MAP.trend,
    actionLabel: 'View Dashboard',
  };
};

const handleCancellation = async () => {
  const insights = await optional(fetchData('insights'), null);
  if (!insights) return pageAnswer('Cancellation Analysis', 'appointment', 'Open Appointments to see cancelled and missed sessions.', ROUTE_MAP.cancellation, 'View Appointments');
  // analytics.service.getCancellationRate gives one alert per therapy with 20+ sessions this
  // month and more than 8% of them marked absent — the worst first, all of them named.
  const alerts = insights.filter(i => i.type === 'cancellation').sort((a, b) => (b.value || 0) - (a.value || 0));
  const cancellationInsight = alerts[0];

  return {
    type: 'data_summary',
    category: 'appointment',
    title: 'Cancellation Analysis',
    message: cancellationInsight
      ? alerts.map(a => a.message).join(' ')
      : 'No cancellation alert this month. One appears when a therapy with 20 or more sessions this month has more than 8% of them marked absent.',
    data: {
      rate: cancellationInsight?.value || 0,
      level: cancellationInsight?.level || 'green',
    },
    route: ROUTE_MAP.cancellation,
    actionLabel: 'View Appointments',
  };
};

const handleAttendance = async () => {
  const insights = await optional(fetchData('insights'), null);
  if (!insights) return pageAnswer('Attendance', 'analytics', 'Staff and patient attendance are both marked on the Attendance page.', ROUTE_MAP.attendance, 'View Attendance');
  const attendanceInsight = insights.find(i => i.type === 'attendance');

  return {
    type: 'data_summary',
    category: 'analytics',
    title: 'Attendance',
    // The staff attendance insight only appears when this month's rate is below 80%.
    message: attendanceInsight
      ? `${attendanceInsight.message} Staff and patient attendance are both marked on the Attendance page.`
      : 'No staff attendance alert this month (one appears when attendance drops below 80%). Staff and patient attendance are both marked on the Attendance page.',
    data: {
      rate: attendanceInsight?.value || null,
    },
    route: ROUTE_MAP.attendance,
    actionLabel: 'View Attendance',
  };
};

const handleSalary = async (parsed, access) => {
  // dashboardInsights leaves the payroll alert out for a role without view_salary, so
  // "no alert" must not be read to them as "everything is paid".
  if (!canOpen(ROUTE_MAP.salary, access)) return noAccessResponse('Salary Status');
  const insights = await optional(fetchData('insights'), null);
  if (!insights) return pageAnswer('Salary Status', 'finance', 'Open Salary Management for this month\'s payroll.', ROUTE_MAP.salary, 'View Payroll');
  const salaryInsight = insights.find(i => i.type === 'salary');

  return {
    type: 'data_summary',
    category: 'finance',
    title: 'Salary Status',
    // getSalaryPayoutStatus says nothing before the due day, so silence is not "all paid".
    message: salaryInsight
      ? salaryInsight.message
      : 'No payroll alert right now. One appears once a month\'s salaries are due and still unpaid.',
    data: {
      pendingCount: salaryInsight?.value || 0,
    },
    route: ROUTE_MAP.salary,
    actionLabel: 'View Payroll',
  };
};

const handleAppointment = async () => {
  // The Dashboard's own figures (dashCounts): today's sessions read the way its Today's
  // Sessions card reads them, and this month's appointments to date — cancelled left out.
  // This used to read a calendar feed that was never fetched outside mock mode.
  const counts = await fetchData('counts');
  const today = counts.todaySessions || 0;
  const b = counts.todaySessionsBreakdown || {};
  const parts = [
    `${b.done || 0} done`,
    `${b.missed || 0} missed`,
    b.off ? `${b.off} centre off / planned leave` : null,
    `${b.pending || 0} still to come`,
  ].filter(Boolean);

  return {
    type: 'data_summary',
    category: 'appointment',
    title: 'Appointment Summary',
    message: `Today: ${today} session(s) — ${parts.join(', ')}. This month so far: ${counts.totalAppointments || 0} appointment(s), cancelled ones not counted.`,
    data: {
      totalSessions: today,
      todayBreakdown: b,
      monthAppointments: counts.totalAppointments || 0,
    },
    route: ROUTE_MAP.appointment,
    actionLabel: 'View Appointments',
  };
};

// ── New feature handlers ───────────────────────────────────────────────────

const handleWaitingList = async () => ({
  type: 'data_summary',
  category: 'patient',
  title: 'Waiting List',
  message: 'Navigate to the Waiting List page to view patients queued for sessions, manage priorities, and assign slots.',
  data: null,
  route: ROUTE_MAP.waiting_list,
  actionLabel: 'View Waiting List',
});

const handleServices = async () => ({
  type: 'data_summary',
  category: 'appointment',
  title: 'Services',
  message: 'Manage all therapy, consultation, and treatment services offered by your clinic. You can add, edit, import, and configure services.',
  data: null,
  route: ROUTE_MAP.services,
  actionLabel: 'View Services',
});

const handleSessionTypes = async () => ({
  type: 'data_summary',
  category: 'appointment',
  title: 'Session Types',
  message: 'Configure how sessions are scheduled — one-time evaluations, recurring therapy, packages, or group sessions.',
  data: null,
  route: ROUTE_MAP.session_types,
  actionLabel: 'View Session Types',
});

const handlePricing = async () => ({
  type: 'data_summary',
  category: 'finance',
  title: 'Pricing Plans',
  message: 'Create and manage patient billing plans — monthly with minimum visits, attendance-based, or fixed visit packages.',
  data: null,
  route: ROUTE_MAP.pricing,
  actionLabel: 'View Pricing Plans',
});

const handleSettlement = async () => ({
  type: 'data_summary',
  category: 'finance',
  title: 'Monthly Settlement',
  message: 'Generate month-end billing invoices. Preview settlement data, verify attendance-based billing, and generate invoices in bulk.',
  data: null,
  route: ROUTE_MAP.settlement,
  actionLabel: 'View Settlement',
});

const handlePredictive = async (parsed) => {
  const query = parsed.raw?.toLowerCase() || '';
  let subRoute = ROUTE_MAP.predictive;
  let title = 'Predictive Intelligence';
  let message = 'AI-powered insights — revenue forecasting, dropout risk detection, therapist performance rankings, and business recommendations.';

  if (query.includes('dropout') || query.includes('risk')) {
    subRoute = '/portal/predictive-intelligence/dropout-risk';
    title = 'Dropout Risk';
    message = 'View patients at risk of dropping out, with risk levels and recommended interventions.';
  } else if (query.includes('forecast')) {
    subRoute = '/portal/predictive-intelligence/revenue-forecast';
    title = 'Revenue Forecast';
    message = 'View predicted revenue with confidence scores and growth trends.';
  } else if (query.includes('advisor') || query.includes('recommend')) {
    subRoute = '/portal/predictive-intelligence/business-advisor';
    title = 'Business Advisor';
    message = 'Get actionable business recommendations based on clinic data patterns.';
  } else if (query.includes('performance')) {
    subRoute = '/portal/predictive-intelligence/therapist-performance';
    title = 'Therapist Performance';
    message = 'View therapist rankings, session counts, and performance tiers.';
  }

  return {
    type: 'data_summary',
    category: 'analytics',
    title,
    message,
    data: null,
    route: subRoute,
    actionLabel: `View ${title}`,
  };
};

const handleClinical = async () => ({
  type: 'data_summary',
  category: 'analytics',
  title: 'Clinical Intelligence',
  message: 'Therapy effectiveness analytics — patient progress scoring, stagnation detection, goal achievement tracking, and clinical insights.',
  data: null,
  route: ROUTE_MAP.clinical,
  actionLabel: 'View Therapy Intelligence',
});

const handleDesignation = async () => ({
  type: 'data_summary',
  category: 'settings',
  title: 'Designations',
  message: 'Manage staff designations and link them to the services they can provide. Create, edit, or delete designations.',
  data: null,
  route: ROUTE_MAP.designation,
  actionLabel: 'View Designations',
});

const handleSupport = async () => ({
  type: 'data_summary',
  category: 'general',
  title: 'Help & Support',
  message: 'Browse FAQs, submit support tickets, or contact our team for assistance.',
  data: null,
  route: ROUTE_MAP.support,
  actionLabel: 'Go to Help & Support',
});

const handleReferrals = async () => ({
  type: 'data_summary',
  category: 'patient',
  title: 'Referrals',
  message: 'See who referred your patients (the Referred By on each patient), how many patients each referrer sent, and import or export the list.',
  data: null,
  route: ROUTE_MAP.referrals,
  actionLabel: 'View Referrals',
});

const handleWalletCredits = async () => ({
  type: 'data_summary',
  category: 'finance',
  title: 'Wallet Credits',
  message: 'See whose wallet got money in a chosen month, and why. Each name opens that patient\'s Wallet tab.',
  data: null,
  route: ROUTE_MAP.wallet_credits,
  actionLabel: 'View Wallet Credits',
});

const handleGuests = async () => ({
  type: 'data_summary',
  category: 'patient',
  title: 'Guest Sessions',
  message: 'Sessions booked for people who are not registered patients. Book one from Appointments ("Not a patient?" in the booking form); the list is under Patients.',
  data: null,
  route: ROUTE_MAP.guests,
  actionLabel: 'View Guests',
});

const handleGroupSessions = async () => ({
  type: 'data_summary',
  category: 'appointment',
  title: 'Group Sessions',
  message: 'Weekly group sessions: each group\'s day, time, therapists and the children in it. The appointment grid shows them and keeps those therapists\' slots blocked.',
  data: null,
  route: ROUTE_MAP.group_sessions,
  actionLabel: 'View Group Sessions',
});

const handleLeave = async () => ({
  type: 'data_summary',
  category: 'analytics',
  title: 'Leave',
  message: 'Staff leave requests, leave types and leave balances — apply for leave, then approve or reject it.',
  data: null,
  route: ROUTE_MAP.leave,
  actionLabel: 'View Leave',
});

const handleRoles = async () => ({
  type: 'data_summary',
  category: 'settings',
  title: 'Roles & Permissions',
  message: 'Choose what each role — Receptionist, Therapist or a role of your own — can see and do. Only an Admin can change this.',
  data: null,
  route: ROUTE_MAP.roles,
  actionLabel: 'View Roles & Permissions',
});

const noAccessResponse = (title) => ({
  type: 'data_summary',
  category: 'general',
  title,
  message: 'Your role doesn\'t have access to this. Ask your Admin if you need it.',
  data: null,
  route: null,
  actionLabel: null,
});

// ── Handler dispatch ────────────────────────────────────────────────────────

const HANDLERS = {
  invoice_filter: handleInvoiceFilter,
  revenue: handleRevenue,
  patient: handlePatient,
  therapist: handleTherapist,
  therapy: handleTherapyQuery,
  expense: handleExpense,
  report: handleReport,
  trend: handleTrend,
  cancellation: handleCancellation,
  attendance: handleAttendance,
  salary: handleSalary,
  appointment: handleAppointment,
  waiting_list: handleWaitingList,
  services: handleServices,
  session_types: handleSessionTypes,
  pricing: handlePricing,
  settlement: handleSettlement,
  predictive: handlePredictive,
  clinical: handleClinical,
  designation: handleDesignation,
  support: handleSupport,
  referrals: handleReferrals,
  wallet_credits: handleWalletCredits,
  guests: handleGuests,
  group_sessions: handleGroupSessions,
  leave: handleLeave,
  roles: handleRoles,
};

// ── Quick commands ──────────────────────────────────────────────────────────

const QUICK_COMMANDS = [
  { label: 'Monthly Report', query: 'generate monthly report' },
  { label: 'Revenue Trend', query: 'revenue trend this month' },
  { label: 'Pending Invoices', query: 'show pending invoices' },
  { label: 'Inactive Patients', query: 'show inactive patients' },
  { label: "Today's Sessions", query: 'appointments today' },
  { label: 'Top Therapist', query: 'top therapist' },
  { label: 'Cancellation Rate', query: 'cancellation rate' },
  { label: 'Waiting List', query: 'waiting list' },
  { label: 'Dropout Risk', query: 'dropout risk' },
  { label: 'Pricing Plans', query: 'pricing plans' },
  { label: 'Settlement', query: 'monthly settlement' },
  { label: 'Referrals', query: 'referrals' },
  { label: 'Wallet Credits', query: 'wallet credits' },
];

// ── Greeting & help detection ───────────────────────────────────────────────

const GREETING_PATTERNS = /^(hi|hello|hey|hii+|hola|namaste|good\s*(morning|afternoon|evening)|sup|yo|howdy)\b/i;
const HELP_PATTERNS = /^(help|what can you do|commands|features|how to use|options|menu|capabilities)\b/i;
const THANKS_PATTERNS = /^(thanks|thank you|thankyou|ty|thx|dhanyavaad|shukriya)\b/i;

const handleGreeting = () => ({
  type: 'greeting',
  category: 'general',
  title: 'Hello!',
  message: "Hi! I'm your Vistaar Assistant. I can help with revenue, patients, invoices, today's sessions, waiting list, pricing plans, settlement, wallet credits, referrals, attendance, leave, salary, predictive intelligence, clinical insights, and more — all without any external AI.",
  data: null,
  route: null,
  actionLabel: null,
  confidence: 98,
  suggestions: QUICK_COMMANDS.slice(0, 4),
});

const handleHelp = () => ({
  type: 'help',
  category: 'general',
  title: 'What I Can Do',
  message: "Here's what you can ask me:",
  data: {
    capabilities: [
      { command: '"Revenue last month"', description: 'View revenue for any period' },
      { command: '"Pending above 5000"', description: 'Filter invoices by amount' },
      { command: '"Inactive patients"', description: 'Find patients by status' },
      { command: '"Appointments today"', description: "Today's sessions and this month's count" },
      { command: '"Top therapist"', description: 'See therapist rankings' },
      { command: '"Speech therapy revenue"', description: 'Therapy-wise breakdown' },
      { command: '"Cancellation rate"', description: 'Appointment cancellations' },
      { command: '"Monthly report"', description: 'Full auto-generated report' },
      { command: '"Revenue trend"', description: 'Trend analysis with confidence' },
      { command: '"Waiting list"', description: 'View waiting list queue' },
      { command: '"Dropout risk"', description: 'Patients at risk of dropout' },
      { command: '"Pricing plans"', description: 'Manage billing plans' },
      { command: '"Monthly settlement"', description: 'Generate month-end invoices' },
      { command: '"Clinical intelligence"', description: 'Therapy effectiveness & progress' },
      { command: '"Services"', description: 'Manage clinic services' },
      { command: '"Attendance"', description: 'Staff & patient attendance' },
      { command: '"Leave"', description: 'Staff leave requests & balances' },
      { command: '"Salary"', description: 'Payroll & salary status' },
      { command: '"Wallet credits"', description: "Whose wallet got money, and why" },
      { command: '"Referrals"', description: 'Who referred your patients' },
      { command: '"Group sessions"', description: 'Weekly groups & their children' },
      { command: '"Guest sessions"', description: 'Sessions for non-patients' },
      { command: '"Roles & permissions"', description: 'What each role can do (Admin)' },
    ],
  },
  route: null,
  actionLabel: null,
  confidence: 98,
  suggestions: QUICK_COMMANDS.slice(0, 4),
});

const handleThanks = () => ({
  type: 'thanks',
  category: 'general',
  title: null,
  message: "You're welcome! Let me know if you need anything else.",
  data: null,
  route: null,
  actionLabel: null,
  confidence: 98,
});

// ── Main execute function ───────────────────────────────────────────────────

/**
 * Execute a natural language command
 * @param {string} query - User's natural language input
 * @param {object} [access] - { isAdmin, permissions } of the signed-in user
 * @returns {Promise<object>} Structured response with data, message, route
 */
export const executeCommand = async (query, access) => {
  const trimmed = query.trim();

  // Check greeting / help / thanks first (before parser)
  if (GREETING_PATTERNS.test(trimmed)) return handleGreeting();
  if (HELP_PATTERNS.test(trimmed)) return handleHelp();
  if (THANKS_PATTERNS.test(trimmed)) return handleThanks();

  const parsed = parseQuery(trimmed);

  if (parsed.type === 'unknown') {
    return {
      type: 'fallback',
      category: 'general',
      title: 'Command Not Recognized',
      message: "I couldn't understand that command. Try asking about revenue, patients, invoices, appointments, or type \"help\" to see what I can do.",
      data: null,
      route: null,
      actionLabel: null,
      confidence: 0,
      suggestions: QUICK_COMMANDS.slice(0, 4),
    };
  }

  const handler = HANDLERS[parsed.type];
  if (!handler) {
    return {
      type: 'fallback',
      category: 'general',
      title: 'Feature Coming Soon',
      message: `I understood your query about "${parsed.type}" but this feature is being built. Try a different command.`,
      data: null,
      route: ROUTE_MAP[parsed.type] || null,
      actionLabel: null,
      confidence: parsed.confidence,
      suggestions: QUICK_COMMANDS.slice(0, 4),
    };
  }

  try {
    const result = await handler(parsed, access);
    // No button to a page this role can't open (PermissionRoute would send it to the Dashboard).
    if (result.route && !canOpen(result.route, access)) {
      return {
        ...result,
        message: result.data
          ? `${result.message} (Your role can't open the page with the details.)`
          : `${result.message} Your role can't open this page — ask your Admin if you need it.`,
        route: null,
        actionLabel: null,
        confidence: parsed.confidence,
        parsedQuery: parsed,
      };
    }
    return {
      ...result,
      confidence: parsed.confidence,
      parsedQuery: parsed,
    };
  } catch (error) {
    if (isForbidden(error)) {
      return { ...noAccessResponse('No Access'), confidence: parsed.confidence };
    }
    return {
      type: 'error',
      category: 'general',
      title: 'Something Went Wrong',
      message: 'Couldn\'t load the data. Please try again or rephrase your query.',
      data: null,
      route: null,
      actionLabel: null,
      confidence: 0,
      suggestions: QUICK_COMMANDS.slice(0, 4),
    };
  }
};

export { QUICK_COMMANDS };
export default executeCommand;
